/**
 * GL2025 theme version of ImportForm
 * When inside a menu (MenuContext):
 *   - Renders as a MenuItem that opens the import dialog
 * When standalone (not in menu):
 *   - Renders as Button like the original
 */
import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary, Button, fromTheme, styled } from 'lib_ui-primitives';
import ActiveRecord from '../../contextProviders/ActiveRecord';
import ActionButton from '../../actionElement/Button';
import SimpleDialog from '../../dialog/SimpleDialog';
import Form from '../../form/Form';
import InnerImportForm from './InnerImportForm';
import FormButton from '../../formButton/FormButton';
import useEventSink from '../../../hooks/useEventSink';
import { metadata } from 'lib_ui-services';
import MenuContext from '../../menuSelection/MenuContext';
import { MenuItem } from '../../menuSelection';

const rc = React.createElement;

const CloseButton = styled(Button)`
    flex-grow: 1;
    min-width: ${fromTheme('modal', 'button', 'minWidth')};
    max-width: ${fromTheme('modal', 'button', 'maxWidth')};
`;

const ImportButton = styled(FormButton)`
    flex-grow: 1;
    min-width: ${fromTheme('modal', 'button', 'minWidth')};
    max-width: ${fromTheme('modal', 'button', 'maxWidth')};
`;

function ImportForm(props) {
    const {
        currentRoute,
        hNode,
        hNode: { id, title, namespace, relation, displayTitleOnButton = false, iconName, size, variant }
    } = props || { hNode: {} };

    const [, publish] = useEventSink();

    // Check if we're inside a menu context
    const menuContext = useContext(MenuContext);
    const isInsideMenu = menuContext?.isInsideMenu || menuContext === true;

    const relationLabel = metadata.getPrettyRelationName(namespace, relation);

    const dialogHNode = {
        id: `dialog${id}`,
        viewport: ['all'],
        hNodeType: 'SimpleDialog',
        hNodeTypeGroup: 'dialog',
        title: title || 'Import ' + relationLabel,
        closeAction: 'close',
        openAction: 'open',
        namespace,
        relation,
        openActionType: { verb: 'open', namespace, relation },
        closeActionType: { verb: 'close', namespace, relation }
    };

    // The trigger element - either MenuItem or Button
    let triggerElement;

    if (isInsideMenu) {
        // Render as MenuItem when inside a menu
        triggerElement = rc(MenuItem, {
            hNode: {
                id: 'openButton' + id,
                forAction: 'open',
                title: title || 'Import ' + relationLabel,
                iconName: iconName ?? 'download',
                namespace,
                relation,
                useEmbeddedVersion: true
            },
            currentRoute
        });
    } else {
        // Render as Button when standalone
        const openButton = {
            title: 'Import',
            id: 'openButton' + id,
            forAction: 'open',
            namespace,
            relation,
            size,
            variant,
            buttonStyle: displayTitleOnButton ? 'AccentOne' : 'IconAction',
            iconName: iconName ?? 'download'
        };
        triggerElement = rc(ActionButton, {
            hNode: openButton,
            currentRoute,
            'data-testid': 'import-button'
        });
    }

    // prettier-ignore
    return rc(Fragment, null,
        triggerElement,
        rc(SimpleDialog, { hNode: dialogHNode, currentRoute },
            rc(ErrorBoundary, null,
                rc(ActiveRecord, { namespace: 'import', relation: 'import', isNew: true },
                    rc(Form, { hNode: { id: `form${id}`, namespace: 'import', relation: 'import' } },
                        rc(InnerImportForm, {
                            ...props,
                            hNode: {
                                ...hNode,
                                title: relationLabel,
                                hNodeType: 'composite',
                                hNodeTypeGroup: 'formElement'
                            }
                        }),
                        rc(CloseButton, {
                            id: `close${id}`,
                            value: 'Close',
                            buttonStyle: 'AccentOne',
                            variant: 'secondary',
                            size:'lg',
                            hNode: {
                                hNodeType: 'FormButton',
                                hNodeBlockGroup: 'formButtons',
                                hNodeTypeGroup: 'formButton'
                            },
                            onClick: () => publish(null, dialogHNode.closeActionType)
                        }),
                        rc(ImportButton, {
                            hNode: {
                                id: `import${id}`,
                                formAction: 'submit',
                                title: 'Import',
                                buttonStyle: 'Positive',
                                size:'lg',
                                variant: 'primary',
                                hNodeType: 'FormButton',
                                hNodeBlockGroup: 'formButtons',
                                hNodeTypeGroup: 'formButton'
                            }
                        })
                    )
                )
            )
        )
    );
}

ImportForm.propTypes = {
    dispatch: PropTypes.func,
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired,
    errors: PropTypes.object,
    children: PropTypes.array,
    relationLabel: PropTypes.string,
    currentRoute: PropTypes.string
};

export default ImportForm;
